import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ButtonSecondary } from "@/components/Buttons";
import api from "@/lib/api";
import { ArrowLeft, Heart, FileText, Loader2, AlertTriangle, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import { toast } from "@/components/ui/sonner";

export default function MaterialDetail() {
    const { id } = useParams();
    const [material, setMaterial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const { user, openAuth } = useAuth();
    const { add, has } = useCart();
    const { isFavorite, toggle } = useFavorites();
    const [selected, setSelected] = useState(0);

    const handleFavorite = async () => {
        if (!material) return;
        if (!user) {
            openAuth("register");
            return;
        }
        try {
            const nowFav = await toggle(material.id);
            toast.success(nowFav ? "Saved to your materials" : "Removed from saved materials", {
                description: material.name,
            });
        } catch {
            toast.error("Could not update saved materials");
        }
    };

    const handleAdd = () => {
        if (!material) return;
        const variantList = material.variants || [];
        if (!variantList.length) return;
        const variant = variantList[selected];
        if (has(material.id, variant)) return;
        add(material, variant);
        toast.success("Added to cart", {
            description: `${material.name}${variant?.label ? ` — ${variant.label}` : ""}`,
        });
    };

    useEffect(() => {
        setLoading(true);
        api.get(`/materials/${id}`)
            .then((res) => setMaterial(res.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center gap-3 text-mist text-sm pt-20">
                <Loader2 className="h-4 w-4 animate-spin text-gold" /> Loading material…
            </div>
        );
    }

    if (notFound || !material) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 pt-20 text-center">
                <p className="font-serif text-3xl font-light text-brand">Material not found</p>
                <ButtonSecondary to="/materials" data-testid="detail-back-btn">
                    Back to Catalogue
                </ButtonSecondary>
            </div>
        );
    }

    const variants = material.variants || [];
    const variant = variants[selected] || null;
    const fav = isFavorite(material.id);
    const inCart = has(material.id, variant);
    // variant-specific product photo (strength band baked into the label); v0 = canonical
    const imgSrc =
        selected > 0 && material.image_url
            ? material.image_url.replace(/\.jpg$/, `-v${selected}.jpg`)
            : material.image_url || "/assets/vial-product.jpg";

    return (
        <div data-testid="material-detail-page" className="pt-32 pb-24 sm:pb-32 px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                <Link
                    to="/materials"
                    data-testid="detail-back-link"
                    className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-wine transition-colors duration-300"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Research Materials
                </Link>

                <Reveal className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Visual */}
                    <div className="relative bg-white border border-stone-200 overflow-hidden">
                        <img
                            src={imgSrc}
                            alt={material.name}
                            onError={(e) => {
                                if (!e.currentTarget.dataset.fbk && material.image_url && e.currentTarget.src !== material.image_url) {
                                    e.currentTarget.dataset.fbk = "1";
                                    e.currentTarget.src = material.image_url;
                                }
                            }}
                            className="w-full h-[380px] object-contain bg-[#F7F3EB]"
                            data-testid="detail-image"
                        />
                        <button
                            type="button"
                            onClick={handleFavorite}
                            aria-label={fav ? "Remove from saved materials" : "Save material"}
                            data-testid="detail-fav-btn"
                            className={`absolute top-4 right-4 h-9 w-9 rounded-full bg-white/90 border flex items-center justify-center transition-colors duration-300 ${
                                fav ? "border-gold text-gold" : "border-stone-200 text-slate-400 hover:text-wine hover:border-gold/60"
                            }`}
                        >
                            <Heart className={`h-4 w-4 ${fav ? "fill-current" : ""}`} strokeWidth={1.5} />
                        </button>
                        <span className="absolute bottom-4 right-4 text-[0.55rem] uppercase tracking-[0.2em] font-semibold text-gold border border-gold/50 bg-white/80 px-2.5 py-1">
                            Research Use Only
                        </span>
                        {material.is_placeholder && (
                            <span className="absolute top-4 left-4 text-[0.55rem] uppercase tracking-[0.2em] font-semibold text-slate-400 border border-dashed border-stone-300 bg-white/80 px-2.5 py-1" data-testid="detail-placeholder-tag">
                                Placeholder
                            </span>
                        )}
                    </div>

                    {/* Info */}
                    <div>
                        <p className="eyebrow">{material.category}</p>
                        <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light tracking-tight text-brand" data-testid="detail-name">
                            {material.name}
                        </h1>
                        {material.description && (
                            <p className="mt-6 text-base leading-relaxed text-mist">{material.description}</p>
                        )}
                        {material.unit && <p className="mt-2 text-xs text-slate-500">{material.unit}</p>}

                        {/* Variant selector */}
                        {variants.length > 0 && (
                            <div className="mt-8" data-testid="detail-variants">
                                <p className="text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
                                    Size / Strength
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {variants.map((v, i) => (
                                        <button
                                            key={v.sku || v.label || i}
                                            type="button"
                                            onClick={() => setSelected(i)}
                                            data-testid={`detail-variant-${i}`}
                                            className={`px-4 py-2 text-xs font-medium border rounded-sm transition-colors duration-200 ${
                                                i === selected
                                                    ? "bg-wine text-white border-wine"
                                                    : "bg-white text-slate-600 border-stone-200 hover:border-gold/60 hover:text-wine"
                                            }`}
                                        >
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-8 divide-y divide-stone-200 border-y border-stone-200 text-sm">
                            <div className="py-4 flex items-center justify-between gap-4">
                                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-400">Price</span>
                                <span className="font-serif text-xl text-brand text-right" data-testid="detail-price">
                                    {variant?.price || "To be announced"}
                                </span>
                            </div>
                            <div className="py-4 flex items-center justify-between gap-4">
                                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-400">Availability</span>
                                <span
                                    className={`inline-flex items-center gap-2 text-right ${
                                        !variants.length ? "text-slate-400" : variant?.available ? "text-emerald-700" : "text-red-500"
                                    }`}
                                    data-testid="detail-availability"
                                >
                                    <span
                                        className={`h-1.5 w-1.5 rounded-full ${
                                            !variants.length ? "bg-slate-300" : variant?.available ? "bg-emerald-500" : "bg-red-400"
                                        }`}
                                    />
                                    {!variants.length ? "Pending catalog data" : variant?.available ? "In Stock" : "Unavailable"}
                                </span>
                            </div>
                            {variant?.sku && (
                                <div className="py-4 flex items-center justify-between gap-4">
                                    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-400">SKU</span>
                                    <span className="text-slate-700 text-right" data-testid="detail-sku">{variant.sku}</span>
                                </div>
                            )}
                            {variant?.lot_batch && (
                                <div className="py-4 flex items-center justify-between gap-4">
                                    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-400">Lot / Batch</span>
                                    <span className="text-slate-700 text-right" data-testid="detail-lot">{variant.lot_batch}</span>
                                </div>
                            )}
                            <div className="py-4 flex items-center justify-between gap-4">
                                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-slate-400">Documentation</span>
                                {variant?.coa_url ? (
                                    <a
                                        href={variant.coa_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="detail-coa-link"
                                        className="inline-flex items-center gap-2 text-wine hover:text-wine-deep transition-colors duration-300"
                                    >
                                        <FileText className="h-4 w-4 shrink-0" />
                                        View COA
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center gap-2 text-slate-400 text-right">
                                        <FileText className="h-4 w-4 shrink-0" />
                                        COA pending
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row gap-4">
                            <button
                                type="button"
                                onClick={handleAdd}
                                disabled={!variants.length || inCart}
                                data-testid="detail-add-to-cart-btn"
                                className={`inline-flex items-center justify-center gap-2 px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold transition-colors duration-300 ${
                                    !variants.length
                                        ? "bg-surface2 text-slate-400 border border-stone-200 cursor-not-allowed"
                                        : inCart
                                          ? "bg-surface2 text-slate-400 border border-stone-200 cursor-default"
                                          : "bg-wine text-white hover:bg-gold-bright"
                                }`}
                            >
                                <ShoppingBag className="h-4 w-4" />
                                {!variants.length ? "Catalog Data Pending" : inCart ? "In Cart" : "Add to Cart"}
                            </button>
                            <ButtonSecondary to="/contact" data-testid="detail-request-coa-btn">
                                Request COA
                            </ButtonSecondary>
                        </div>

                        <div className="mt-8 flex items-start gap-3 border border-gold/30 bg-gold/5 rounded-sm px-5 py-4">
                            <AlertTriangle className="h-4 w-4 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                            <p className="text-[0.7rem] leading-relaxed text-slate-600" data-testid="detail-disclaimer">
                                {material.research_use_disclaimer} Not intended for human or veterinary use,
                                consumption, administration, diagnosis, treatment, or prevention of disease.
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </div>
    );
}
